import { wpClient } from '../lib/wordpress';
import { gql } from '@apollo/client';

const GET_ALL_HACKS = gql`
  query GetAllHacks {
    disabilityHacks {
      nodes {
        databaseId
        title
        categories {
          nodes {
            name
          }
        }
        disabilityHacksImport {
          title
          platform
          videoUrl
          summary
          hackDescription
          otKeywords {
            name
          }
          source
          userTested
          otUsefulnessRating
          hackType
          reviewedBy
          difficultyLevel
          featured
          submittedByEmail
        }
      }
    }
  }
`;

export interface Hack {
  id: number;
  title: string;
  categories: {
    nodes: { name: string }[];
  };
  disabilityHacksImport: {
    title: string;
    platform: string;
    videoUrl: string;
    summary: string;
    hackDescription: string;
    otKeywords: { name: string }[];
    source: string;
    userTested: string;
    otUsefulnessRating: number;
    hackType: string;
    reviewedBy: string;
    difficultyLevel: string;
    featured: boolean;
    submittedByEmail: string;
  };
}

export const hackService = {
  async getAllHacks(): Promise<Hack[]> {
    try {
      const { data } = await wpClient.query({
        query: GET_ALL_HACKS
      });

      return data.disabilityHacks.nodes.map((post: any) => ({
        id: post.databaseId,
        title: post.title,
        categories: post.categories || { nodes: [] },
        disabilityHacksImport: post.disabilityHacksImport || {}
      }));
    } catch (error) {
      console.error('Error fetching hacks:', error);
      return [];
    }
  },

  async getCategories(): Promise<string[]> {
    try {
      const hacks = await this.getAllHacks();
      const categories = new Set<string>();
      hacks.forEach(hack => {
        if (hack.categories?.nodes) {
          hack.categories.nodes.forEach(node => {
            if (node.name) categories.add(node.name);
          });
        }
      });
      return Array.from(categories).sort();
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  async searchHacks(query: string): Promise<Hack[]> {
    try {
      const hacks = await this.getAllHacks();
      const searchTerms = query.toLowerCase().split(' ');
      return hacks.filter(hack => {
        const searchableText = [
          hack.title,
          hack.disabilityHacksImport.hackDescription,
          ...(hack.categories?.nodes.map(n => n.name) || []),
          ...(hack.disabilityHacksImport.otKeywords?.map(k => k.name) || [])
        ].join(' ').toLowerCase();
        return searchTerms.every(term => searchableText.includes(term));
      });
    } catch (error) {
      console.error('Error searching hacks:', error);
      return [];
    }
  }
};

// Sample data for development
export const sampleHacks: Hack[] = [
  {
    id: 1,
    title: "One-Handed Jar Opener",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "One-Handed Jar Opener",
      platform: "Kitchen",
      videoUrl: "https://example.com/jar-opener",
      summary: "A simple device that holds jars firmly, allowing them to be opened with one hand. Perfect for individuals with limited grip strength or one-hand dominance.",
      hackDescription: "A simple device that holds jars firmly, allowing them to be opened with one hand. Perfect for individuals with limited grip strength or one-hand dominance.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 2,
    title: "Ergonomic Pencil Grip",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "Ergonomic Pencil Grip",
      platform: "Writing",
      videoUrl: "https://example.com/pencil-grip",
      summary: "An ergonomic silicone grip that slides onto pencils and pens to improve writing control for those with fine motor difficulties.",
      hackDescription: "An ergonomic silicone grip that slides onto pencils and pens to improve writing control for those with fine motor difficulties.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 3,
    title: "Voice Command Guide",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "Voice Command Guide",
      platform: "Technology",
      videoUrl: "https://example.com/voice-commands",
      summary: "A comprehensive guide to setting up and using voice commands on smartphones for hands-free operation.",
      hackDescription: "A comprehensive guide to setting up and using voice commands on smartphones for hands-free operation.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 4,
    title: "DIY Reacher Tool",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "DIY Reacher Tool",
      platform: "Mobility",
      videoUrl: "https://example.com/diy-reacher",
      summary: "Instructions for making a low-cost reacher tool using household items, perfect for those with limited mobility or reach.",
      hackDescription: "Instructions for making a low-cost reacher tool using household items, perfect for those with limited mobility or reach.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 5,
    title: "Adaptive Gardening Tools",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "Adaptive Gardening Tools",
      platform: "Gardening",
      videoUrl: "https://example.com/gardening-tools",
      summary: "A collection of modified gardening tools with ergonomic handles and wrist supports for individuals with arthritis or grip challenges.",
      hackDescription: "A collection of modified gardening tools with ergonomic handles and wrist supports for individuals with arthritis or grip challenges.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 6,
    title: "Weighted Eating Utensils",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "Weighted Eating Utensils",
      platform: "Kitchen",
      videoUrl: "https://example.com/weighted-utensils",
      summary: "Specially designed utensils with weighted handles to reduce tremors while eating, providing greater independence at mealtimes.",
      hackDescription: "Specially designed utensils with weighted handles to reduce tremors while eating, providing greater independence at mealtimes.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 7,
    title: "Button Helper",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "Button Helper",
      platform: "Clothing",
      videoUrl: "https://example.com/button-helper",
      summary: "A simple device that aids in buttoning shirts and pants for those with limited dexterity or one-handed dressing.",
      hackDescription: "A simple device that aids in buttoning shirts and pants for those with limited dexterity or one-handed dressing.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  },
  {
    id: 8,
    title: "Sensory-Friendly Lighting Guide",
    categories: {
      nodes: []
    },
    disabilityHacksImport: {
      title: "Sensory-Friendly Lighting Guide",
      platform: "Sensory",
      videoUrl: "https://example.com/lighting-guide",
      summary: "Comprehensive guidance on setting up home lighting to reduce sensory overload and create a comfortable environment.",
      hackDescription: "Comprehensive guidance on setting up home lighting to reduce sensory overload and create a comfortable environment.",
      otKeywords: [],
      source: "",
      userTested: "",
      otUsefulnessRating: 0,
      hackType: "",
      reviewedBy: "",
      difficultyLevel: "",
      featured: false,
      submittedByEmail: ""
    }
  }
];

export async function getCategories(): Promise<string[]> {
  try {
    const hacks = await hackService.getAllHacks();
    const categories = new Set<string>();
    hacks.forEach(hack => {
      if (hack.categories?.nodes) {
        hack.categories.nodes.forEach(node => {
          if (node.name) categories.add(node.name);
        });
      }
    });
    return Array.from(categories).sort();
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}
